import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { translationsEN } from '@/locales/en';
import { translationsES } from '@/locales/es';

type Language = 'en' | 'es';

const STORAGE_KEY = 'resuena_language';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

const getStoredLanguage = (): Language => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'en' || stored === 'es') return stored;
  } catch {
    // localStorage not available (SSR, private mode, etc.)
  }
  // Default to Spanish for Resuena's target market (Argentina/Latam)
  return 'es';
};

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(getStoredLanguage);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // localStorage not available
    }
  };

  // Sync with system preference changes (optional, good UX)
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    // Only auto-detect if user hasn't explicitly chosen
    if (!stored) {
      const browserLang = navigator.language?.startsWith('es') ? 'es' : 'en';
      setLanguage(browserLang);
    }
  }, []);

  const t = (key: string): string => {
    const translations = language === 'en' ? translationsEN : translationsES;
    return (translations as Record<string, string>)[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
